import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { openSummaryPanel } from '../lib/events';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { Bell, CalendarDays, Bot, AlertTriangle, CheckCheck, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../lib/logger';

const POLL_MS = 45000;
// NOTE: these keys hold ONLY non-sensitive UI preferences ('on'/'off') — never
// tokens or personal data — so plain localStorage is appropriate here.
const SOUND_KEY = 'futurists_notif_sound';   // default ON
const PUSH_KEY = 'futurists_notif_push';     // default OFF until user enables

/** Best-effort debug logging for non-fatal notification plumbing errors. */
const logDebug = (context, err) => logger.debug(`[notifications] ${context}:`, err?.message || err);

const typeIcon = (t) => {
  if (t?.startsWith('google') || t === 'notetaker_error') return <AlertTriangle size={13} className="text-orange-500 shrink-0 mt-0.5" />;
  if (t === 'bot_lobby' || t === 'meeting_transcript_ready') return <Bot size={13} className="text-primary shrink-0 mt-0.5" />;
  return <CalendarDays size={13} className="text-primary shrink-0 mt-0.5" />;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

/** Soft two-tone chime synthesized with the Web Audio API — no audio asset needed. */
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [660, 0.14]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.36);
    });
    setTimeout(() => ctx.close().catch((e) => logDebug('audio context close', e)), 900);
  } catch (e) {
    logDebug('chime blocked until first user interaction', e); // expected pre-gesture
  }
};

const showBrowserPush = (n) => {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const note = new Notification(n.title || 'Futurists CRM', {
      body: n.message || '', tag: n.id, icon: '/favicon.ico',
    });
    note.onclick = () => {
      window.focus();
      if (n.link_type === 'meeting' && n.link_id) openSummaryPanel('meeting', n.link_id);
      else if (n.link_type === 'project' && n.link_id) window.location.assign(`/projects/${n.link_id}`);
      note.close();
    };
  } catch (e) {
    logDebug('browser push failed (best-effort)', e);
  }
};

/** Ask the browser for push permission; returns true when granted. */
const requestPushPermission = async () => {
  if (!('Notification' in window)) {
    toast.error('This browser does not support desktop alerts');
    return false;
  }
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm !== 'granted') {
    toast.error('Browser alerts blocked — allow notifications for this site in your browser settings');
    return false;
  }
  return true;
};

const NotificationRow = ({ n, onOpen }) => (
  <button type="button" onClick={() => onOpen(n)}
    className={`w-full text-left rounded-lg border p-2.5 transition-colors hover:bg-muted/60 ${n.read_at ? 'opacity-70' : 'bg-primary/5 border-primary/20'}`}
    data-testid={`notification-${n.id}`}>
    <div className="flex items-start gap-2">
      {typeIcon(n.type)}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold truncate">{n.title}</span>
          <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
        </div>
        <div className="text-[11px] text-muted-foreground line-clamp-2">{n.message}</div>
      </div>
      {!n.read_at && <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1" aria-label="Unread" />}
    </div>
  </button>
);

/** Seen-notification ids shared across ALL mounted bells (desktop + mobile
 * headers each render one) so a new item triggers exactly ONE chime/push,
 * never a double-fire echo. null = no poll yet (baseline only, no alerts). */
let sharedSeenIds = null;

/** Consume the poll result: returns ONLY the never-seen unread items and
 * updates the shared seen-set (first poll baselines silently). */
const detectNewItems = (items) => {
  const unreadNew = sharedSeenIds === null ? []
    : items.filter((n) => !n.read_at && !sharedSeenIds.has(n.id));
  sharedSeenIds = new Set([...(sharedSeenIds || []), ...items.map((n) => n.id)]);
  return unreadNew;
};

/** Sound + browser-push preferences and new-item detection for the bell. */
const useNotificationAlerts = () => {
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off');
  const [push, setPush] = useState(() => localStorage.getItem(PUSH_KEY) === 'on'
    && typeof Notification !== 'undefined' && Notification.permission === 'granted');

  const toggleSound = (v) => {
    setSound(v);
    localStorage.setItem(SOUND_KEY, v ? 'on' : 'off');
    if (v) playChime();
  };

  const togglePush = async (v) => {
    if (v && !(await requestPushPermission())) return;
    setPush(v);
    localStorage.setItem(PUSH_KEY, v ? 'on' : 'off');
    if (v) toast.success('Browser alerts on — you will get a desktop ping for new meeting events');
  };

  /** Alert (chime + push) once per new unread item across all mounted bells. */
  const handleNewItems = useCallback((items) => {
    const unreadNew = detectNewItems(items);
    if (!unreadNew.length) return;
    if (sound) playChime();
    if (push) unreadNew.slice(0, 3).forEach(showBrowserPush);
  }, [sound, push]);

  return { sound, toggleSound, push, togglePush, handleNewItems };
};

/** Feed state: polling, on-focus refresh, mark-read and item navigation. */
const useNotificationFeed = (handleNewItems, closePanel) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setItems(data.items || []);
      setUnread(data.unread || 0);
      handleNewItems(data.items || []);
    } catch (e) {
      logDebug('poll failed (will retry on next interval)', e); // no toast — polling noise
    }
  }, [handleNewItems]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const openItem = (n) => {
    if (!n.read_at) {
      api.post('/notifications/read', { ids: [n.id] }).then(load)
        .catch((e) => logDebug('mark-read failed', e));
    }
    closePanel();
    if (n.link_type === 'meeting' && n.link_id) openSummaryPanel('meeting', n.link_id);
    else if (n.link_type === 'project' && n.link_id) window.location.assign(`/projects/${n.link_id}`);
    else if (n.link_type === 'projects') window.location.assign('/projects');
    else if (n.link_type === 'settings') window.location.assign('/meetings/settings');
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read', { all: true });
      load();
    } catch (e) {
      logDebug('mark-all-read failed', e);
      toast.error('Could not mark notifications as read — please try again');
    }
  };

  return { items, unread, openItem, markAll };
};

/** forwardRef + prop spread are required so Radix `asChild` can wire the trigger. */
const BellTrigger = React.forwardRef(({ unread, ...triggerProps }, ref) => (
  <Button ref={ref} {...triggerProps} variant="ghost" size="icon" className="relative" data-testid="notification-bell" aria-label="Notifications">
    <Bell size={17} />
    {unread > 0 && (
      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center"
        data-testid="notification-unread-count">
        {unread > 99 ? '99+' : unread}
      </span>
    )}
  </Button>
));
BellTrigger.displayName = 'BellTrigger';

const PanelHeader = ({ sound, toggleSound, unread, markAll }) => (
  <div className="flex items-center justify-between px-1 pb-2">
    <span className="text-xs font-heading font-semibold">Notifications</span>
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => toggleSound(!sound)}
        className="text-muted-foreground hover:text-foreground" data-testid="notification-sound-toggle"
        title={sound ? 'Chime on — click to mute' : 'Chime off — click to unmute'}
        aria-label={sound ? 'Mute notification sound' : 'Unmute notification sound'}>
        {sound ? <Volume2 size={13} /> : <VolumeX size={13} />}
      </button>
      {unread > 0 && (
        <button type="button" onClick={markAll} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid="notification-mark-all">
          <CheckCheck size={11} /> Mark all read
        </button>
      )}
    </div>
  </div>
);

const NotificationList = ({ items, onOpen }) => (
  <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-0.5">
    {items.length === 0 && (
      <div className="text-xs text-muted-foreground text-center py-8" data-testid="notification-empty">
        You're all caught up — meeting alerts will appear here
      </div>
    )}
    {items.map((n) => <NotificationRow key={n.id} n={n} onOpen={onOpen} />)}
  </div>
);

/** In-app notification bell: unread badge + feed for meeting events, Google
 * connection alerts and Fireflies transcript activity. Polls every 45s, plays a
 * soft chime and (optionally) fires desktop push alerts for new items. */
export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { sound, toggleSound, push, togglePush, handleNewItems } = useNotificationAlerts();
  const { items, unread, openItem, markAll } = useNotificationFeed(handleNewItems, () => setOpen(false));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <BellTrigger unread={unread} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] max-w-[92vw] p-2" data-testid="notification-panel">
        <PanelHeader sound={sound} toggleSound={toggleSound} unread={unread} markAll={markAll} />
        <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-2.5 py-1.5 mb-2">
          <span className="text-[11px] text-muted-foreground">Desktop alerts (browser push)</span>
          <Switch checked={push} onCheckedChange={togglePush} data-testid="notification-push-toggle" />
        </div>
        <NotificationList items={items} onOpen={openItem} />
      </PopoverContent>
    </Popover>
  );
};
